import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '../ui/Dialog';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';
import { LAYERS } from '../ui/layers';
import { MIN_REASON } from '../../utils/handoffs';

/**
 * Why a handoff is cancelled or declined (B6): the server keeps a reason of
 * at least MIN_REASON characters and shows it to the other side.
 * `onSubmit(reason)` may return a promise; a rejection's message is shown.
 */
export default function ReasonDialog({ title, confirmLabel, onSubmit, onClose, testId = 'handoff-reason' }) {
  const { t } = useTranslation(['handoffs', 'common']);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState('');
  const short = reason.trim().length < MIN_REASON;

  const submit = async () => {
    if (short) return;
    setBusy(true);
    setFailure('');
    try {
      await onSubmit?.(reason.trim());
    } catch (e) {
      setFailure(e?.message || t('form.failed'));
      setBusy(false);
    }
  };

  return (
    <Dialog title={title} onClose={onClose} busy={busy} dirty={!!reason.trim()} layer={LAYERS.confirm} width={420}
      testId={testId}>
      <label style={st.label}>
        <span>{t('panel.reason')}</span>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} maxLength={1000}
          style={st.input} data-testid={`${testId}-text`} />
        <span style={st.hint}>{t('panel.reasonHint')}</span>
      </label>
      {failure && <p role="alert" style={st.failure} data-testid={`${testId}-failure`}>{failure}</p>}
      <ActionBar>
        <Button onClick={onClose} disabled={busy} title={t('tip.cancel')}>{t('common:cancel')}</Button>
        <Button variant="danger" onClick={submit} disabled={busy || short} title={t('tip.cancelHandoff')}
          data-testid={`${testId}-submit`}>
          {busy ? t('panel.confirming') : (confirmLabel || t('panel.confirm'))}
        </Button>
      </ActionBar>
    </Dialog>
  );
}

const st = {
  label: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12, fontSize: 14 },
  input: { padding: '8px 10px', borderRadius: 6, border: '1px solid var(--c-border)', font: 'inherit',
    background: 'var(--c-surface)', color: 'var(--c-surface-fg)' },
  hint: { fontSize: 13, color: 'var(--c-text-muted-fg)' },
  failure: { fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', padding: '8px 10px',
    borderRadius: 6, margin: '0 0 12px' },
};
